import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Divider, Checkbox, FormGroup, FormControlLabel, Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GroupIcon from '@mui/icons-material/Group';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f8f9fa',
  minHeight: '100vh',
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
  maxWidth: '1100px',
  margin: '0 auto',
  backgroundColor: '#fff',
  border: '1px solid #eef2f6',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const EditClient = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    // Business Information
    business_name: '',
    category: '',
    gst_number: '',
    website: '',
    tags: '',
    lead_source: '',
    // Contact Details
    contact_person: '',
    first_name: '',
    last_name: '',
    email: '',
    contact_email: '',
    phone_number: '',
    alt_number: '',
    whatsapp: '',
    // Services
    is_gmb_services: false,
    is_smo_services: false,
    is_website_service: false,
    is_other_services: false,
    // Address
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'India',
    // Management Details
    package: '',
    cro_name: '',
    bdm_name: '',
    welcome_call: false,
    // Other
    type: '',
    comment: '',
    status: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId) return;
      setFetchLoading(true);
      setError('');
      try {
        // Try to fetch from leads API first
        const res = await ApiService.get(`/admin/leads/get-leads/${clientId}`);
        const root = res?.data ?? res;

        // Extract the lead data
        const extractLead = (payload) => {
          if (!payload) return null;
          if (payload.id) return payload;
          if (payload.data?.id) return payload.data;
          if (payload.lead?.id) return payload.lead;
          if (Array.isArray(payload)) return payload.find(l => String(l.id) === String(clientId)) || payload[0];
          if (Array.isArray(payload.data)) return payload.data.find(l => String(l.id) === String(clientId)) || payload.data[0];
          if (Array.isArray(payload.leads)) return payload.leads.find(l => String(l.id) === String(clientId)) || payload.leads[0];
          return null;
        };

        const item = extractLead(root);
        if (!item) throw new Error('Client not found');

        logger.log('[EditClient] Fetched lead data:', item);

        // Populate form with all fields
        setForm({
          // Business Information
          business_name: item.business_name || item.company_name || '',
          category: item.category || '',
          gst_number: item.gst_number || '',
          website: item.website || '',
          tags: item.tags || '',
          lead_source: item.lead_source || item.source || '',
          // Contact Details
          contact_person: item.contact_person || '',
          first_name: item.first_name || '',
          last_name: item.last_name || '',
          email: item.email || '',
          contact_email: item.contact_email || item.email || '',
          phone_number: item.phone_number || '',
          alt_number: item.alt_number || '',
          whatsapp: item.whatsapp || item.phone_number || '',
          // Services
          is_gmb_services: !!item.is_gmb_services,
          is_smo_services: !!item.is_smo_services,
          is_website_service: !!item.is_website_service,
          is_other_services: !!item.is_other_services,
          // Address
          address_line1: item.address_line1 || '',
          address_line2: item.address_line2 || '',
          city: item.city || '',
          state: item.state || '',
          zip_code: item.zip_code || '',
          country: item.country || 'India',
          // Management Details
          package: item.package || '',
          cro_name: item.cro_name || '',
          bdm_name: item.bdm_name || '',
          welcome_call: !!item.welcome_call,
          // Other
          type: item.type || '',
          comment: item.comment || '',
          status: !!item.status,
        });
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to load client data';
        setError(msg);
        logger.error('[EditClient] Fetch error:', e);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchClientData();
  }, [clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        // Business Information
        business_name: form.business_name,
        category: form.category,
        gst_number: form.gst_number,
        website: form.website,
        tags: form.tags,
        lead_source: form.lead_source,
        // Contact Details
        contact_person: form.contact_person,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        contact_email: form.contact_email || form.email,
        phone_number: form.phone_number,
        alt_number: form.alt_number,
        whatsapp: form.whatsapp,
        // Services
        is_gmb_services: form.is_gmb_services,
        is_smo_services: form.is_smo_services,
        is_website_service: form.is_website_service,
        is_other_services: form.is_other_services,
        // Address
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        country: form.country,
        // Management Details
        package: form.package,
        cro_name: form.cro_name,
        bdm_name: form.bdm_name,
        welcome_call: form.welcome_call,
        // Other
        type: form.type,
        comment: form.comment,
        status: form.status,
      };

      // Remove empty/null fields
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) {
          // Don't delete boolean fields or status
          if (typeof payload[k] !== 'boolean') {
            delete payload[k];
          }
        }
      });

      await ApiService.put(`/admin/leads/update-leads/${clientId}`, payload);
      setSuccess('Client updated successfully!');
      setTimeout(() => navigate(`/admin-dashboard/client/${clientId}`), 1000);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to update client';
      setError(msg);
      logger.error('[EditClient] Update error:', e);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h4" fontWeight={700} gutterBottom>Edit Client</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Update all client details including business info, contact, services, and management details.
      </Typography>

      <FormContainer>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <form onSubmit={handleSubmit}>
          {/* Business Information Section */}
          <SectionHeader>
            <BusinessIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Business Information</Typography>
          </SectionHeader>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Name"
                name="business_name"
                value={form.business_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Category"
                name="category"
                value={form.category}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST Number"
                name="gst_number"
                value={form.gst_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={form.website}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tags"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="Comma separated tags"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Lead Source</InputLabel>
                <Select
                  name="lead_source"
                  value={form.lead_source}
                  label="Lead Source"
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Source</MenuItem>
                  <MenuItem value="Facebook">Facebook</MenuItem>
                  <MenuItem value="Instagram">Instagram</MenuItem>
                  <MenuItem value="Google">Google</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                  <MenuItem value="Cold Call">Cold Call</MenuItem>
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Contact Details Section */}
          <SectionHeader>
            <PersonIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Contact Details</Typography>
          </SectionHeader>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contact_person"
                value={form.contact_person}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                name="contact_email"
                type="email"
                value={form.contact_email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Alternate Phone"
                name="alt_number"
                value={form.alt_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="WhatsApp"
                name="whatsapp"
                value={form.whatsapp}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Services Section */}
          <SectionHeader>
            <StorefrontIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Services</Typography>
          </SectionHeader>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: '12px', mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={form.is_gmb_services} onChange={handleChange} name="is_gmb_services" color="success" />}
              label="GMB Services"
            />
            <FormControlLabel
              control={<Checkbox checked={form.is_smo_services} onChange={handleChange} name="is_smo_services" color="primary" />}
              label="SMO Services"
            />
            <FormControlLabel
              control={<Checkbox checked={form.is_website_service} onChange={handleChange} name="is_website_service" color="secondary" />}
              label="Website Service"
            />
            <FormControlLabel
              control={<Checkbox checked={form.is_other_services} onChange={handleChange} name="is_other_services" color="warning" />}
              label="Other Services"
            />
          </Box>

          {/* Address Section */}
          <SectionHeader>
            <LocationOnIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Address</Typography>
          </SectionHeader>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                name="address_line1"
                value={form.address_line1}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                name="address_line2"
                value={form.address_line2}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="zip_code"
                value={form.zip_code}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Management Details Section */}
          <SectionHeader>
            <GroupIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Management Details</Typography>
          </SectionHeader>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Selected Plan</InputLabel>
                <Select
                  name="package"
                  value={form.package}
                  label="Selected Plan"
                  onChange={handleChange}
                >
                  <MenuItem value="">No Plan</MenuItem>
                  <MenuItem value="basic">Basic Plan</MenuItem>
                  <MenuItem value="advance">Advance Plan</MenuItem>
                  <MenuItem value="pro">Pro Plan</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CRO Name"
                name="cro_name"
                value={form.cro_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="BDM Name"
                name="bdm_name"
                value={form.bdm_name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Client Type</InputLabel>
                <Select
                  name="type"
                  value={form.type}
                  label="Client Type"
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Type</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Quarterly">Quarterly</MenuItem>
                  <MenuItem value="Six-Month">Six-Month</MenuItem>
                  <MenuItem value="Annually">Annually</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={form.welcome_call} onChange={handleChange} name="welcome_call" color="success" />}
                label="Welcome Call Completed"
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Checkbox checked={form.status} onChange={handleChange} name="status" color="success" />}
                label="Client Active (Converted)"
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>

          {/* Comments Section */}
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Comments / Notes"
              name="comment"
              multiline
              rows={3}
              value={form.comment}
              onChange={handleChange}
              placeholder="Add any additional notes about this client..."
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => navigate(-1)}
              disabled={loading}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 4 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                px: 5,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </FormContainer>
    </Container>
  );
};

export default EditClient;
